import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './InputRange.scss';

const InputRange = ({
    defaultValue = [0, 100],
    min = 0,
    max = 100,
    step = 1,
    moreThanMax = false,
    lessThanMin = false,
    onChange = () => {},
    className = '',
    unit = '',
    showLabels = true,
}) => {
    const [range, setRange] = useState(defaultValue);
    const [activeSlider, setActiveSlider] = useState(null); // 'min' ou 'max'
    const sliderRef = useRef(null);

    // Fonction pour mettre à jour les styles CSS custom properties
    const updateStyles = () => {
        if (sliderRef.current) {
            const percentage1 = ((range[0] - min) / (max - min)) * 100;
            const percentage2 = ((range[1] - min) / (max - min)) * 100;
            
            sliderRef.current.style.setProperty('--range-left', `${percentage1}%`);
            sliderRef.current.style.setProperty('--range-right', `${percentage2}%`);
            
            // Calcul compensé pour les positions des labels
            const thumbSize = 18; // Correspond à $thumb-size dans le SCSS
            const containerWidth = sliderRef.current.offsetWidth;
            const halfThumb = thumbSize / 2;
            
            // Position réelle du centre du curseur
            const thumbCenter1 = (percentage1 / 100) * (containerWidth - thumbSize) + halfThumb;
            const thumbCenter2 = (percentage2 / 100) * (containerWidth - thumbSize) + halfThumb;
            
            // Position des labels (peut être ajustée pour éviter le débordement)
            let labelPosition1 = thumbCenter1;
            let labelPosition2 = thumbCenter2;
            
            // Obtenir les éléments de label pour mesurer leurs largeurs
            const labelMin = sliderRef.current.querySelector('.input-range__thumb-label--min');
            const labelMax = sliderRef.current.querySelector('.input-range__thumb-label--max');
            
            if (labelMin && labelMax) {
                const labelMinWidth = labelMin.offsetWidth;
                const labelMaxWidth = labelMax.offsetWidth;
                
                // Prévenir le débordement à gauche pour le premier label
                const minPadding = labelMinWidth / 2;
                if (labelPosition1 < minPadding) {
                    labelPosition1 = minPadding;
                }
                
                // Prévenir le débordement à droite pour le deuxième label
                const maxPadding = labelMaxWidth / 2;
                if (labelPosition2 > containerWidth - maxPadding) {
                    labelPosition2 = containerWidth - maxPadding;
                }
                
                // Gérer la collision entre labels si trop proches
                const minGapBetweenLabels = 10;
                const totalLabelWidth = labelMinWidth / 2 + labelMaxWidth / 2 + minGapBetweenLabels;
                
                if (labelPosition2 - labelPosition1 < totalLabelWidth) {
                    const midPoint = (labelPosition1 + labelPosition2) / 2;
                    labelPosition1 = midPoint - totalLabelWidth / 2;
                    labelPosition2 = midPoint + totalLabelWidth / 2;
                    
                    // Vérifier à nouveau les limites
                    if (labelPosition1 < minPadding) {
                        labelPosition1 = minPadding;
                        labelPosition2 = labelPosition1 + totalLabelWidth;
                    }
                    if (labelPosition2 > containerWidth - maxPadding) {
                        labelPosition2 = containerWidth - maxPadding;
                        labelPosition1 = labelPosition2 - totalLabelWidth;
                    }
                }
            }
            
            // Convertir en pourcentages
            const labelPercentage1 = (labelPosition1 / containerWidth) * 100;
            const labelPercentage2 = (labelPosition2 / containerWidth) * 100;
            
            // Appliquer les positions des labels
            sliderRef.current.style.setProperty('--label-left', `${labelPercentage1}%`);
            sliderRef.current.style.setProperty('--label-right', `${labelPercentage2}%`);
        }
    };

    useEffect(() => {
        updateStyles();
        onChange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range]);

    useEffect(() => {
        updateStyles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Gestionnaire de redimensionnement pour recalculer les positions
    useEffect(() => {
        const handleResize = () => {
            updateStyles();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMinChange = (e) => {
        const value = parseInt(e.target.value, 10);
        const newValue = value > range[1] ? range[1] : value;
        setRange([newValue, range[1]]);
    };

    const handleMaxChange = (e) => {
        const value = parseInt(e.target.value, 10);
        const newValue = value < range[0] ? range[0] : value;
        setRange([range[0], newValue]);
    };

    // Gestionnaires pour gérer les z-index quand les curseurs se chevauchent
    const handleMinFocus = () => {
        setActiveSlider('min');
    };

    const handleMaxFocus = () => {
        setActiveSlider('max');
    };

    const handleMinMouseDown = () => {
        setActiveSlider('min');
    };

    const handleMaxMouseDown = () => {
        setActiveSlider('max');
    };

    const handleBlur = () => {
        setActiveSlider(null);
    };

    return (
        <div className={`input-range ${className}`}>
            <div className="input-range__container" ref={sliderRef}>
                {showLabels && (
                    <>
                        <div className="input-range__thumb-label input-range__thumb-label--min">
                            {range[0]}{unit}{ lessThanMin && range[0] <= min ? '-' : ''}
                        </div>
                        <div className="input-range__thumb-label input-range__thumb-label--max">
                            {range[1]}{unit}{ moreThanMax && range[1] >= max ? '+' : ''}
                        </div>
                    </>
                )}

                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={range[0]}
                    onChange={handleMinChange}
                    onInput={updateStyles} // Mise à jour en temps réel
                    onFocus={handleMinFocus}
                    onMouseDown={handleMinMouseDown}
                    onBlur={handleBlur}
                    className={`input-range__slider input-range__slider--min ${activeSlider === 'min' ? 'input-range__slider--active' : ''}`}
                    aria-label={`Valeur minimale: ${range[0]}${unit}`}
                />
                
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={range[1]}
                    onChange={handleMaxChange}
                    onInput={updateStyles} // Mise à jour en temps réel
                    onFocus={handleMaxFocus}
                    onMouseDown={handleMaxMouseDown}
                    onBlur={handleBlur}
                    className={`input-range__slider input-range__slider--max ${activeSlider === 'max' ? 'input-range__slider--active' : ''}`}
                    aria-label={`Valeur maximale: ${range[1]}${unit}`}
                />
            </div>
        </div>
    );
};

InputRange.propTypes = {
    defaultValue: PropTypes.arrayOf(PropTypes.number),
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    onChange: PropTypes.func,
    className: PropTypes.string,
    label: PropTypes.string,
    unit: PropTypes.string,
    showLabels: PropTypes.bool,
    showValues: PropTypes.bool,
};

export default InputRange;